import { Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { ImperiumTemplate } from '../../api/documentApi'
import { Client } from '../../redux/reducers/clients'
import { tokens } from '../../styles/tokens'

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  field: {
    flexGrow: 1,
    minWidth: '180px',
  },
  sectionTitle: {
    margin: 0,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
    fontWeight: 600,
    color: tokens.color.neutral[700],
  },
  checklist: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 0,
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
}))

const CLIENT_TYPE_FO = 'individual'
const CLIENT_TYPE_PO = 'company'

// Datum z Portilia -> hodnota pro <input type="date">.
// BE posila birthDate jako retezec "DD.MM.YYYY HH:mm" (DateUtils), ostatni datumy jako ISO.
const toDateInputValue = (value: Date | string | null | undefined): string => {
  if (!value) return ''
  if (typeof value === 'string') {
    const czech = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
    if (czech) return `${czech[3]}-${czech[2]}-${czech[1]}`
  }
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return format(date, 'yyyy-MM-dd')
}

interface Props {
  templates: ImperiumTemplate[]
  client: Partial<Client> | null
  loading: boolean
  onGenerate: (payload: { dokumenty: string[]; data: Record<string, any> }, format: 'pdf' | 'docx') => void
}

// Port formulare imperium-generatoru (index.html) — klice dat odpovidaji 1:1 API generatoru.
export const ImperiumDocumentForm: React.FC<Props> = ({ templates, client, loading, onGenerate }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([])
  const [data, setData] = useState<Record<string, string>>({
    clientType: CLIENT_TYPE_FO,
    fo_statni_obcanstvi: 'Česká republika',
    fo_druh_dokladu: 'Občanský průkaz',
    fo_telefon: '+420 ',
    po_koresp_stat: 'Česká republika',
    po_zastupce_statni_obcanstvi: 'Česká republika',
    po_zastupce_druh_dokladu: 'Občanský průkaz',
    po_zastupce_telefon: '+420 ',
  })

  useEffect(() => {
    if (!client) return
    const fullName = [client.firstName, client.surname].filter(Boolean).join(' ')
    setData(prev => ({
      ...prev,
      clientType: client.company ? CLIENT_TYPE_PO : CLIENT_TYPE_FO,
      fo_jmeno_prijmeni: client.company ? '' : fullName,
      fo_datum_narozeni: toDateInputValue(client.birthDate),
      fo_rodne_cislo: client.personalNumber || '',
      fo_misto_narozeni: client.birthPlace || '',
      fo_trvale_ulice: client.permanentAddress?.street || '',
      fo_trvale_mesto: client.permanentAddress?.town || '',
      fo_trvale_psc: client.permanentAddress?.zipCode || '',
      fo_koresp_ulice: client.contactAddress?.street || '',
      fo_koresp_mesto: client.contactAddress?.town || '',
      fo_koresp_psc: client.contactAddress?.zipCode || '',
      fo_cislo_op: client.identityCardNumber || '',
      fo_platnost_op: toDateInputValue(client.identityCardValidity),
      fo_op_vystavil: client.identityCardIssuer || '',
      fo_telefon: client.telephoneNumber || prev.fo_telefon,
      fo_email: client.email || '',
      po_nazev_spolecnosti: client.companyName || '',
      po_ico: client.companyId || '',
      po_sidlo_ulice: client.companyAddress?.street || '',
      po_sidlo_mesto: client.companyAddress?.town || '',
      po_sidlo_psc: client.companyAddress?.zipCode || '',
      po_koresp_ulice: client.contactAddress?.street || '',
      po_koresp_mesto: client.contactAddress?.town || '',
      po_koresp_psc: client.contactAddress?.zipCode || '',
    }))
  }, [client])

  const setField = (name: string) => (event: React.ChangeEvent<{ value: string }>) => {
    setData(prev => ({ ...prev, [name]: event.target.value }))
  }

  const isCompany = data.clientType === CLIENT_TYPE_PO

  const toggleDocument = (id: string) => () => {
    setSelectedDocuments(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]))
  }

  const toggleAll = () => {
    setSelectedDocuments(prev => (prev.length === templates.length ? [] : templates.map(template => template.id)))
  }

  const handleGenerate = (format2: 'pdf' | 'docx') => () => {
    // Skryta sekce se neposila — stejne chovani jako vymazatVstupySekce v generatoru
    const cleaned: Record<string, string> = {}
    Object.keys(data).forEach(key => {
      if (isCompany && key.startsWith('fo_')) return
      if (!isCompany && key.startsWith('po_')) return
      cleaned[key] = data[key]
    })
    onGenerate({ dokumenty: selectedDocuments, data: cleaned }, format2)
  }

  const textField = (name: string, label: string, extra: any = {}) => (
    <TextField
      variant="standard"
      className={classes.field}
      label={label}
      value={data[name] || ''}
      onChange={setField(name)}
      {...extra}
    />
  )

  const selectField = (name: string, label: string, options: string[]) => (
    <TextField
      select
      variant="standard"
      className={classes.field}
      label={label}
      value={data[name] || ''}
      onChange={setField(name)}
    >
      {options.map(option => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  )

  const personFields = (prefix: 'fo' | 'po_zastupce') => (
    <>
      <div className={classes.row}>
        {prefix === 'fo' && textField('fo_jmeno_prijmeni', 'Jméno a příjmení', { placeholder: 'Jan Novák' })}
        {prefix === 'po_zastupce' && textField('po_zastupce_jmeno_prijmeni', 'Jméno a příjmení zástupce')}
        {prefix === 'po_zastupce' && textField('po_zastupce_funkce', 'Funkce (např. Jednatel)')}
        {textField(`${prefix}_datum_narozeni`, 'Datum narození', { type: 'date', InputLabelProps: { shrink: true } })}
        {textField(`${prefix}_rodne_cislo`, 'Rodné číslo', { placeholder: 'RRMMDD/XXXX', inputProps: { maxLength: 11 } })}
        {selectField(`${prefix}_pohlavi`, 'Pohlaví', ['Muž', 'Žena'])}
      </div>
      <div className={classes.row}>
        {textField(`${prefix}_statni_obcanstvi`, 'Státní občanství')}
        {textField(`${prefix}_misto_narozeni`, 'Místo narození')}
      </div>
      <div className={classes.row}>
        {textField(`${prefix}_trvale_ulice`, 'Ulice a č.p. (trvalé bydliště)')}
        {textField(`${prefix}_trvale_mesto`, 'Město')}
        {textField(`${prefix}_trvale_psc`, 'PSČ')}
      </div>
      <div className={classes.row}>
        {selectField(`${prefix}_druh_dokladu`, 'Druh dokladu', ['Občanský průkaz', 'Cestovní pas', 'Povolení k pobytu'])}
        {textField(`${prefix}_cislo_op`, 'Číslo dokladu')}
        {textField(`${prefix}_platnost_op`, 'Platnost do', { type: 'date', InputLabelProps: { shrink: true } })}
        {textField(`${prefix}_op_vystavil`, 'Doklad vystavil (Úřad)')}
      </div>
      <div className={classes.row}>
        {textField(`${prefix}_telefon`, 'Telefon')}
        {textField(`${prefix}_email`, 'Email', { type: 'email' })}
      </div>
    </>
  )

  return (
    <div className={classes.form}>
      <p className={classes.sectionTitle}>{t('DOCUMENTS')}</p>
      <FormControlLabel
        control={
          <Checkbox
            checked={templates.length > 0 && selectedDocuments.length === templates.length}
            indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < templates.length}
            onChange={toggleAll}
          />
        }
        label={t('SELECT_ALL')}
      />
      <div className={classes.checklist}>
        {templates.map(template => (
          <FormControlLabel
            key={template.id}
            control={<Checkbox checked={selectedDocuments.includes(template.id)} onChange={toggleDocument(template.id)} />}
            label={template.label}
          />
        ))}
      </div>

      <div className={classes.row}>
        <TextField
          select
          variant="standard"
          className={classes.field}
          label={t('CLIENT')}
          value={data.clientType}
          onChange={setField('clientType')}
        >
          <MenuItem value={CLIENT_TYPE_FO}>Fyzická osoba (FO)</MenuItem>
          <MenuItem value={CLIENT_TYPE_PO}>Právnická osoba (PO)</MenuItem>
        </TextField>
      </div>

      {!isCompany && (
        <>
          <p className={classes.sectionTitle}>Fyzická osoba</p>
          {personFields('fo')}
          <div className={classes.row}>
            {textField('fo_koresp_ulice', 'Ulice a č.p. (korespondenční)')}
            {textField('fo_koresp_mesto', 'Město')}
            {textField('fo_koresp_psc', 'PSČ')}
          </div>
        </>
      )}

      {isCompany && (
        <>
          <p className={classes.sectionTitle}>Právnická osoba</p>
          <div className={classes.row}>
            {textField('po_nazev_spolecnosti', 'Název společnosti')}
            {textField('po_ico', 'IČO')}
          </div>
          <div className={classes.row}>
            {textField('po_sidlo_ulice', 'Sídlo — ulice a č.p.')}
            {textField('po_sidlo_mesto', 'Město')}
            {textField('po_sidlo_psc', 'PSČ')}
          </div>
          <div className={classes.row}>
            {textField('po_koresp_ulice', 'Korespondenční — ulice a č.p.')}
            {textField('po_koresp_mesto', 'Město')}
            {textField('po_koresp_psc', 'PSČ')}
            {textField('po_koresp_stat', 'Stát')}
          </div>
          <p className={classes.sectionTitle}>Osoba zastupující klienta</p>
          {personFields('po_zastupce')}
        </>
      )}

      <div className={classes.buttons}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || selectedDocuments.length === 0}
          onClick={handleGenerate('pdf')}
        >
          {t('DOWNLOAD_PDF')}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={loading || selectedDocuments.length === 0}
          onClick={handleGenerate('docx')}
        >
          {t('DOWNLOAD_DOCX')}
        </Button>
        {loading && <CircularProgress size={24} />}
      </div>
    </div>
  )
}
